import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as Sectors } from "../../../assets/icons/sector.svg";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from '../../../lib/helper';
import theme from "../../../theme";
import { TagGroup } from "../../../types/common";
import { Dashboard } from '../../../types/files';
import TagGroups from '../../atoms/TagGroups';

const useStyles = makeStyles(() => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 'auto',
            maxWidth: '100%',
            minWidth: 400,
            height: 'fit-content',
            minHeight: '30vh',
            maxHeight: '70vh',
            padding: '16px 24px 8px 24px',
            boxShadow: '0px 20px 30px -5px rgba(16, 24, 40, 0.06)',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        gap: 16,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '100%',
        overflow: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    content: {
        padding: '24px',
        width: '100%',
        maxHeight: '500px',
        overflow: 'auto',
    },
    deleteBtn: {
        minWidth: '0 !important',
        padding: '0 !important',
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.error['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            background: '#fff !important',
        }
    },
    cancelBtn: {
        height: '40px',
        width: '88px',
        padding: '8px 24px',
        borderRadius: '40px',
        backgroundColor: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveBtn: {
        width: 100,
        height: 40,
        borderRadius: 40,
        backgroundColor: theme.colors.primary['600'],
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}))

const ManageTagsModal: FC<{
    open: boolean;
    filterType?: string;
    onClose: () => void;
}> = ({ open, filterType, onClose }) => {
    const classes = useStyles();
    const { settingsLoaded, tagGroups, setTagGroups } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const [tagGroup, setTagGroup] = useState<TagGroup | undefined>(undefined);

    const handleUpdateTagGroups = useCallback((title: string, tags?: string[], tagsCount?: number, context?: string, create?: boolean) => {
        if (!create) {
            const lookUpTagGroup = tagGroups.find(group => group.title.toLowerCase() === title.toLowerCase());

            if (!lookUpTagGroup) {
                setTagGroups(prev => [...prev, ({ title, tags, tagsPerDashboard: tagsCount || 3, additionalContextQuery: context || '' } as TagGroup)]
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            }  else {
                setTagGroups(prev => prev.map(tagGroup => tagGroup.title.toLowerCase() === title.toLowerCase()
                    ? ({ ...tagGroup, tags: tags ?? [], tagsPerDashboard: tagsCount || 3, additionalContextQuery: context || '' }) : tagGroup)
                        .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            }
        }
        setTagGroup({ title: title, tags: tags ?? [], tagsPerDashboard: tagsCount || 3, additionalContextQuery: context || '' } as TagGroup);
    // eslint-disable-next-line
    }, [tagGroups]);

    const handleEditTitle = useCallback((oldTitle: string, newTitle: string, create?: boolean) => {
        if (!create) {
            setTagGroups(prev => prev.map(tagGroup => tagGroup.title.toLowerCase() === oldTitle.toLowerCase()
                ? ({ ...tagGroup, title: newTitle.trim()  }) : tagGroup)
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));
            setTagGroup(prev => ({ ...prev, title: newTitle.trim() } as TagGroup));

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedTagGroup = false;

                const updatedDashboard = { ...dashboard,
                    tagGroups: dashboard.tagGroups?.map(group => {
                        if (group.title.toLowerCase() === oldTitle.toLowerCase()) {
                            hasRenamedTagGroup = true;
                            return ({ ...group, title: newTitle.trim() });
                        }

                        return group;
                    }) ?? [] };

                if (hasRenamedTagGroup)
                    updatedDashboards.push(updatedDashboard);
            });

            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
           handleUpdateTagGroups(newTitle.trim(), [], 3);
        }
    }, [setTagGroups, dashboards, setDashboards, handleUpdateTagGroups]);

    const handleUpdateContext = useCallback(() => {
        if (filterType && tagGroup!.title.trim().toLowerCase() !== filterType.toLowerCase()) {
            setTagGroups(prev => prev.map(tg => tg.title.toLowerCase() === filterType.toLowerCase()
                ? ({ title: tagGroup!.title.trim(), tags: tagGroup!.tags, tagsPerDashboard: tagGroup!.tagsPerDashboard, additionalContextQuery: tagGroup!.additionalContextQuery || '' }) : tg)
                    .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({title, tags, tagsPerDashboard, additionalContextQuery})));

            const updatedDashboards: Dashboard[] = [];
            dashboards.forEach(dashboard => {
                let hasRenamedTagGroup = false;
                const updatedDashboard = { ...dashboard,
                    tagGroups: dashboard.tagGroups?.map(group => {
                        if (group.title.toLowerCase() === filterType.toLowerCase()) {
                            hasRenamedTagGroup = true;
                            return ({ ...group, title: tagGroup!.title.trim() });
                        }
                        return group;
                    }) ?? [] };
                if (hasRenamedTagGroup)
                    updatedDashboards.push(updatedDashboard);
            });
            if (!!updatedDashboards.length) {
                Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc(dashboard)))
                    .then(() => setDashboards(prev => prev.map(dashboard =>
                        updatedDashboards.find(updated => updated.id === dashboard.id) ?? dashboard)));
            }
        } else {
            handleUpdateTagGroups(tagGroup!.title, tagGroup!.tags, tagGroup!.tagsPerDashboard, tagGroup!.additionalContextQuery || '');
        }
        onClose();
    }, [tagGroup, filterType, setTagGroups, dashboards, setDashboards, handleUpdateTagGroups, onClose]);

    const handleDeleteTagGroup = useCallback((title: string) => {
        setTagGroups(prev => prev.filter(tagGroup => tagGroup.title.toLowerCase() !== title.toLowerCase())
            .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({ title, tags, tagsPerDashboard, additionalContextQuery })));
        onClose();
    }, [onClose, setTagGroups]);

    useEffect(() => {
        if (settingsLoaded) {
            if (!!filterType) {
                if (!!tagGroups.length)
                    setTagGroup(prev => tagGroups.find(group => group?.title === filterType) ?? prev);
            } else {
                handleUpdateTagGroups('', [], 3, '', true);
            }
        }
    // eslint-disable-next-line
    }, [settingsLoaded]);

    if (!tagGroup)
        return (<></>);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Sectors className={classes.headerIcon} />
                <span>{'Custom tags'}</span>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0', width: '100%' }} />
            <DialogContent className={classes.dialogContent}>
                {!tagGroup.title && (
                    <Typography color={theme.colors.neutral['600']} pl={1}>
                        {'Tags get automatically assigned using AI to new deals.'}
                    </Typography>
                )}
                <TagGroups group={tagGroup.title}
                    tags={tagGroup.tags}
                    tagsCount={tagGroup.tagsPerDashboard || 3}
                    context={tagGroup.additionalContextQuery || ''}
                    onContext={(newContext) => setTagGroup(prev => ({ ...prev, additionalContextQuery: newContext } as TagGroup))}
                    onUpdateTags={(tags, count) => handleUpdateTagGroups(tagGroup.title, tags, count, tagGroup.additionalContextQuery || '')}
                    onEditTitle={(newTitle) => handleEditTitle(tagGroup.title, newTitle, tagGroup.title === '')}
                    onDelete={() => handleDeleteTagGroup(tagGroup.title)}
                    create={tagGroup.title === ''} />
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    {!!tagGroup.title && !tagGroup.title.toLowerCase().includes('sector') && (
                        <Button variant="text"
                            className={classes.deleteBtn}
                            startIcon={<DeleteIcon fontSize="medium" sx={{ color: theme.colors.error['500'] }} />}
                            onClick={() => handleDeleteTagGroup(tagGroup.title)}
                            disableRipple> {'Delete tags'} </Button>
                    )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <Button className={classes.cancelBtn} onClick={onClose}> {'Back'} </Button>
                    <Button variant="contained" className={classes.saveBtn}
                        onClick={handleUpdateContext}
                        disabled={!tagGroup?.additionalContextQuery}> {'Save'}  </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

export default ManageTagsModal;